<template>
  <article class="nebraska-myday-lottery-play-slip-item flex flex-col items-center w-full px-100">
    <LcCard class="play" shadow="none">
      <div class="flex flex-col items-center w-full gap-y-200">
        <header class="flex flex-col items-center pt-100 gap-100">
          <h4 class="text-h4">
            {{ $t('lottery.nebraskamydaylottery.playslip.selector.headline') }}
          </h4>
          <p>
            {{ $t('lottery.nebraskamydaylottery.playslip.selector.message') }}
          </p>
        </header>

        <LcCard class="container w-full">
          <div class="flex flex-col items-center -m-200">
            <ul class="flex w-full">
              <li
                v-for="({ name }, index) in fieldConfig"
                :key="`li_${play.id}_${index}_${name}`"
                class="w-full"
              >
                <LcSlotWheel
                  v-if="options[getField(name)].length > 0"
                  :key="`wheel_${play.id}_${name}`"
                  ref="slotWheel"
                  :options="options[getField(name)]"
                  :value="values[getField(name)]"
                  @options-changed="() => onOptionsChanged(name)"
                  @re-init="() => onReInit(name)"
                  @ready="onReady"
                  @update:value="(newValue) => onUpdate(name, newValue, index)"
                >
                  <template #option="{ option }">
                    <div
                      :key="`${play.id}_${name}_${option.value}`"
                      :aria-disabled="option.value === null"
                      :aria-label="getAriaLabel(option, name)"
                    >
                      {{ option.label }}
                    </div>
                  </template>
                </LcSlotWheel>
              </li>
            </ul>
          </div>
        </LcCard>
      </div>
    </LcCard>
  </article>
</template>

<script lang="ts" setup>
import type { LcSlotWheel, LcSlotWheelOption } from '@lottocom/frontend-components'

import { getDaysInMonth } from 'date-fns'

import type { PlaySlipPlay } from '#product/app/composables/playSlip'

import { getField } from '../utils/fieldMapping'

type DateRecord<T> = Record<'day' | 'month' | 'year', T>

type NebraskaMyDayLotteryPlaySlipItemEmits = {
  (event: 'play-state-changed', isDirty: boolean, playId: string): void
}

type NebraskaMyDayLotteryPlaySlipItemProps = {
  defaultValue?: Record<string, number>
  disabled?: boolean
  play: PlaySlipPlay
}

const MAP_NAME_TO_INDEX = {
  day: 1,
  month: 0,
  year: 2,
}

const emit = defineEmits<NebraskaMyDayLotteryPlaySlipItemEmits>()
const { defaultValue, play } = defineProps<NebraskaMyDayLotteryPlaySlipItemProps>()

const { t } = useI18n()
const { fieldConfig, setSelectedNumbersForField, visiblePlayId } = usePlaySlip()

const slotWheelRefs = useTemplateRef('slotWheel')

const isReinitializing = ref(false)
const options = ref<DateRecord<LcSlotWheelOption[]>>({
  day: [],
  month: [],
  year: [],
})
const values = ref<DateRecord<LcSlotWheelOption['value']>>({
  day: play.fields[1].selected[0] ?? null,
  month: play.fields[0].selected[0] ?? null,
  year: play.fields[2].selected[0] ?? null,
})

const isPlayComplete = computed(() => play.isComplete)

const generateNumbers = (min: number, max: number, leadingZero = false) => [
  {
    label: '-',
    value: null,
  },
  ...Array.from({ length: max - min + 1 }, (_, i) => {
    const index = i + min
    return {
      label: leadingZero ? index.toString().padStart(2, '0') : index.toString(),
      value: index,
    }
  }),
]

const getAriaLabel = (option: LcSlotWheelOption, field: string) =>
  option.value === null
    ? t('lottery.nebraskamyday.scrollwheel.arialabel.ariadisabled', {
        value: getField(field),
      })
    : t('lottery.nebraskamyday.scrollwheel.arialabel', {
        option: option.value,
        value: getField(field),
      })

const getDaysForMonth = (month: number, year?: number) => {
  const fullYear = year ? 2000 + year : 2000
  return getDaysInMonth(new Date(fullYear, month - 1))
}

const getValue = (numbers: number[]) => (numbers.length > 0 ? numbers[0] : null)

const isLeapYear = (year: number) => year % 4 === 0

const onOptionsChanged = (field: string) => {
  if (getField(field) !== 'day') return
  isReinitializing.value = true
}

const onReady = () => {
  const isFirstPlayEmpty = play.fields.every((field) => field.selected.length === 0)
  if (play.id !== 'play-1' || !defaultValue || !isFirstPlayEmpty) return

  Object.entries(defaultValue).forEach(([key, value]) => {
    slotWheelRefs.value?.[MAP_NAME_TO_INDEX[key as keyof typeof MAP_NAME_TO_INDEX]]?.moveToOption(
      value,
      true,
    )
  })
}

const onReInit = async (field: string) => {
  if (getField(field) !== 'day') return
  await nextTick()
  isReinitializing.value = false
}

const onUpdate = (field: string, newValue: LcSlotWheelOption['value'], index: number) => {
  if (visiblePlayId.value !== play.id) return

  const updateFebruaryOptions = (year: number) => {
    const newDayOptionsLength = isLeapYear(year) ? 29 : 28
    options.value.day = generateNumbers(1, newDayOptionsLength)

    return newDayOptionsLength
  }

  const checkLastDayOfMonth = (lastDayOfMonth: number) => {
    if (play.fields[1].selected[0] > lastDayOfMonth) {
      setSelection(1, lastDayOfMonth)
    }
  }

  if (getField(field) === 'month') {
    if (!newValue) {
      setSelection(index, newValue)
      return
    }

    const lastDayOfMonth = getDaysForMonth(newValue, play.fields[2].selected[0])
    options.value.day = generateNumbers(1, lastDayOfMonth)
    checkLastDayOfMonth(lastDayOfMonth)
  }

  // Check for a leap year, if the year gets changed and february is selected
  if (
    getField(field) === 'year' &&
    typeof newValue === 'number' &&
    play.fields[0].selected[0] === 2
  ) {
    const lastDayOfMonth = updateFebruaryOptions(newValue)
    checkLastDayOfMonth(lastDayOfMonth)
  }

  setSelection(index, newValue)
}

const setSelection = (index: number, newValue: Maybe<number>) => {
  const value = newValue === null ? [] : [newValue]
  if (play.fields[index].selected[0] === newValue) return

  setSelectedNumbersForField(play.id, index, value)
}

// Generate options for each lottery field
watchOnce(
  fieldConfig,
  (config) => {
    config.forEach((field) => {
      const min = Math.min(...field.selectableNumbers)

      if (getField(field.name) === 'day') {
        const month = play.fields[0].selected.length ? play.fields[0].selected[0] : 1
        const maxDaysForMonth = getDaysForMonth(month, play.fields[2].selected[0])
        options.value.day = generateNumbers(min, maxDaysForMonth)

        return
      }

      options.value[getField(field.name)] = generateNumbers(
        min,
        Math.max(...field.selectableNumbers),
        getField(field.name) === 'year',
      )
    })
  },
  { immediate: true },
)

watch(isPlayComplete, () => {
  emit('play-state-changed', isPlayComplete.value, play.id)
})

watchDeep(
  () => play.fields,
  async (newValue, oldValue) => {
    if (isDeepEqual(newValue, oldValue)) return

    newValue.forEach(({ name, selected }, index) => onUpdate(name, getValue(selected), index))
    await until(isReinitializing).toBe(false)
    newValue.forEach(({ name, selected }) => {
      values.value[getField(name)] = getValue(selected)
    })
  },
)
</script>

<style lang="scss" scoped>
.nebraska-myday-lottery-play-slip-item {
  position: relative;

  .play {
    position: relative;
    width: 100%;
    max-width: 22.5rem;

    .container {
      overflow: hidden;
    }
  }
}
</style>
